import { useEffect, useMemo, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, Info, ShieldCheck, AlertCircle, Calendar, MapPin,
} from "lucide-react";

// Employee Preferences & Restrictions (read/write, no automation).
// Captured for FUTURE scheduling automation. Does NOT currently affect rota,
// leave decisions, swaps, or assignments. Admins only.

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const RESTRICTION_KINDS = [
  { value: "medical",     label: "Medical",     tone: "red"    },
  { value: "contractual", label: "Contractual", tone: "blue"   },
  { value: "personal",    label: "Personal",    tone: "amber"  },
  { value: "religious",   label: "Religious",   tone: "purple" },
  { value: "family",      label: "Family",      tone: "green"  },
  { value: "other",       label: "Other",       tone: "slate"  },
];
const KIND_BY_VALUE = Object.fromEntries(RESTRICTION_KINDS.map((k) => [k.value, k]));

const EMPTY_RESTRICTION = {
  label: "",
  kind: "other",
  starts_on: "",
  ends_on: "",
  notes: "",
};

export default function EmployeePreferencesTab({ employeeId }) {
  const [prefs, setPrefs] = useState(null);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [busy, setBusy] = useState(false);

  // Restriction dialog state
  const [resOpen, setResOpen] = useState(false);
  const [editingRes, setEditingRes] = useState(null);
  const [resForm, setResForm] = useState(EMPTY_RESTRICTION);
  const [resBusy, setResBusy] = useState(false);

  const load = async () => {
    try {
      const [{ data: p }, { data: c }, { data: l }] = await Promise.all([
        api.get(`/employees/${employeeId}/preferences`),
        api.get("/categories"),
        api.get("/locations"),
      ]);
      setPrefs(p);
      setCategories(c);
      setLocations(l);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Failed to load preferences");
    }
  };
  useEffect(() => { if (employeeId) load(); /* eslint-disable-next-line */ }, [employeeId]);

  const set = (patch) => setPrefs((prev) => ({ ...prev, ...patch }));

  const toggleDay = (key, day) => {
    const list = new Set(prefs[key] || []);
    if (list.has(day)) list.delete(day);
    else list.add(day);
    set({ [key]: Array.from(list).sort((a, b) => a - b) });
  };

  const toggleCategory = (key, catId) => {
    const list = new Set(prefs[key] || []);
    if (list.has(catId)) list.delete(catId);
    else list.add(catId);
    set({ [key]: Array.from(list) });
  };

  const toggleLocation = (key, locId) => {
    const list = new Set(prefs[key] || []);
    if (list.has(locId)) list.delete(locId);
    else list.add(locId);
    set({ [key]: Array.from(list) });
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        preferred_start_time: prefs.preferred_start_time || null,
        preferred_end_time: prefs.preferred_end_time || null,
        preferred_days: prefs.preferred_days || [],
        avoided_days: prefs.avoided_days || [],
        max_consecutive_days: prefs.max_consecutive_days ?? null,
        min_rest_hours: prefs.min_rest_hours ?? null,
        preferred_category_ids: prefs.preferred_category_ids || [],
        avoided_category_ids: prefs.avoided_category_ids || [],
        default_location_id: prefs.default_location_id || null,
        preferred_location_ids: prefs.preferred_location_ids || [],
        allowed_location_ids: prefs.allowed_location_ids || [],
        restricted_location_ids: prefs.restricted_location_ids || [],
        notes: prefs.notes || null,
      };
      const { data } = await api.put(`/employees/${employeeId}/preferences`, payload);
      setPrefs(data);
      toast.success("Preferences saved");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  const acknowledge = async () => {
    try {
      const { data } = await api.post(`/employees/${employeeId}/preferences/acknowledge`);
      setPrefs(data);
      toast.success("Acknowledgement recorded");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const openAddRestriction = () => {
    setEditingRes(null);
    setResForm(EMPTY_RESTRICTION);
    setResOpen(true);
  };
  const openEditRestriction = (r) => {
    setEditingRes(r);
    setResForm({
      label: r.label || "",
      kind: r.kind || "other",
      starts_on: r.starts_on || "",
      ends_on: r.ends_on || "",
      notes: r.notes || "",
    });
    setResOpen(true);
  };

  const submitRestriction = async (e) => {
    e.preventDefault();
    if (!resForm.label.trim()) {
      toast.error("Label is required");
      return;
    }
    setResBusy(true);
    try {
      const body = {
        label: resForm.label.trim(),
        kind: resForm.kind,
        starts_on: resForm.starts_on || null,
        ends_on: resForm.ends_on || null,
        notes: resForm.notes || null,
      };
      if (editingRes) {
        await api.patch(
          `/employees/${employeeId}/preferences/restrictions/${editingRes.restriction_id}`,
          body
        );
        toast.success("Restriction updated");
      } else {
        await api.post(`/employees/${employeeId}/preferences/restrictions`, body);
        toast.success("Restriction added");
      }
      setResOpen(false);
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setResBusy(false);
    }
  };

  const removeRestriction = async (r) => {
    if (!window.confirm(`Remove restriction "${r.label}"?`)) return;
    try {
      await api.delete(
        `/employees/${employeeId}/preferences/restrictions/${r.restriction_id}`
      );
      toast.success("Restriction removed");
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const activeCategories = useMemo(
    () => categories.filter((c) => c.is_active !== false),
    [categories]
  );

  // Locations are already filtered server-side to exclude archived ones.
  const activeLocations = locations;

  // Conflict awareness, if both allowed and restricted contain the same id,
  // restricted wins (server semantic). The UI shows a small note when this happens.
  const conflictingLocIds = useMemo(() => {
    if (!prefs) return [];
    const a = new Set(prefs.allowed_location_ids || []);
    return (prefs.restricted_location_ids || []).filter((id) => a.has(id));
  }, [prefs]);

  if (!prefs) {
    return <div className="py-8 text-center text-sm text-[#4B5773]">Loading…</div>;
  }

  return (
    <div data-testid="employee-preferences-tab" className="space-y-5">
      {/* Disclaimer banner, strictly captured, no enforcement today. */}
      <div className="flex items-start gap-2 p-3 rounded-lg bg-[#F6F9FE] border border-[#DBE7FE] text-xs text-[#1F2A44]"
        data-testid="emp-pref-disclaimer">
        <Info className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[#2563EB]" />
        <div>
          Tell us each employee&apos;s preferred and avoided shift patterns,
          categories and locations so managers can take them into account when
          planning rotas. Managers always make the final scheduling decisions.
        </div>
      </div>

      {/* Working preferences */}
      <Section title="When this person likes to work">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Preferred start">
            <Input type="time" step="1800" value={prefs.preferred_start_time || ""}
              onChange={(e) => set({ preferred_start_time: e.target.value })}
              data-testid="emp-pref-start"
              className="h-10 rounded-lg" />
          </Field>
          <Field label="Preferred end">
            <Input type="time" step="1800" value={prefs.preferred_end_time || ""}
              onChange={(e) => set({ preferred_end_time: e.target.value })}
              data-testid="emp-pref-end"
              className="h-10 rounded-lg" />
          </Field>
        </div>
        <div className="mt-3">
          <Label className="eyebrow">Preferred days</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {DAY_LABELS.map((d, i) => {
              const on = (prefs.preferred_days || []).includes(i);
              return (
                <button type="button" key={`pref-${d}`}
                  data-testid={`emp-pref-day-${d}`}
                  onClick={() => toggleDay("preferred_days", i)}
                  className={`px-3 py-1 rounded-full border text-sm transition-colors ${
                    on
                      ? "bg-[#2563EB] text-white border-[#2563EB]"
                      : "bg-white border-slate-200 text-[#4B5773] hover:bg-slate-50"
                  }`}>{d}</button>
              );
            })}
          </div>
        </div>
        <div className="mt-3">
          <Label className="eyebrow">Avoided days</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {DAY_LABELS.map((d, i) => {
              const on = (prefs.avoided_days || []).includes(i);
              return (
                <button type="button" key={`avoid-${d}`}
                  data-testid={`emp-avoid-day-${d}`}
                  onClick={() => toggleDay("avoided_days", i)}
                  className={`px-3 py-1 rounded-full border text-sm transition-colors ${
                    on
                      ? "bg-red-100 text-red-700 border-red-300"
                      : "bg-white border-slate-200 text-[#4B5773] hover:bg-slate-50"
                  }`}>{d}</button>
              );
            })}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <Field label="Max consecutive days">
            <Input type="number" min="0" step="1"
              value={prefs.max_consecutive_days ?? ""}
              onChange={(e) => set({
                max_consecutive_days: e.target.value === "" ? null : Number(e.target.value),
              })}
              data-testid="emp-pref-max-consec"
              className="h-10 rounded-lg" placeholder="e.g. 5" />
          </Field>
          <Field label="Min rest hours">
            <Input type="number" min="0" step="1"
              value={prefs.min_rest_hours ?? ""}
              onChange={(e) => set({
                min_rest_hours: e.target.value === "" ? null : Number(e.target.value),
              })}
              data-testid="emp-pref-min-rest"
              className="h-10 rounded-lg" placeholder="e.g. 11" />
          </Field>
        </div>
      </Section>

      {/* Assignment preferences */}
      <Section title="Work this person prefers">
        {activeCategories.length === 0 ? (
          <p className="text-xs text-[#4B5773]">No assignment categories yet.</p>
        ) : (
          <>
            <Label className="eyebrow">Preferred categories</Label>
            <div className="flex flex-wrap gap-1.5 mt-2 mb-3" data-testid="emp-pref-cats">
              {activeCategories.map((c) => {
                const on = (prefs.preferred_category_ids || []).includes(c.category_id);
                return (
                  <button type="button" key={`p-${c.category_id}`}
                    data-testid={`emp-pref-cat-${c.category_id}`}
                    onClick={() => toggleCategory("preferred_category_ids", c.category_id)}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                      on
                        ? "bg-[#2563EB] text-white border-[#2563EB]"
                        : "bg-white border-slate-200 text-[#4B5773] hover:bg-slate-50"
                    }`}>{c.name}</button>
                );
              })}
            </div>
            <Label className="eyebrow">Avoided categories</Label>
            <div className="flex flex-wrap gap-1.5 mt-2" data-testid="emp-avoid-cats">
              {activeCategories.map((c) => {
                const on = (prefs.avoided_category_ids || []).includes(c.category_id);
                return (
                  <button type="button" key={`a-${c.category_id}`}
                    data-testid={`emp-avoid-cat-${c.category_id}`}
                    onClick={() => toggleCategory("avoided_category_ids", c.category_id)}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                      on
                        ? "bg-red-100 text-red-700 border-red-300"
                        : "bg-white border-slate-200 text-[#4B5773] hover:bg-slate-50"
                    }`}>{c.name}</button>
                );
              })}
            </div>
          </>
        )}
      </Section>

      {/* Location preferences */}
      <Section title="Where this person works">
        {activeLocations.length === 0 ? (
          <p className="text-xs text-[#4B5773]" data-testid="emp-loc-empty">
            No locations defined yet. Add them in <strong>Setup → Locations</strong>{" "}
            to capture where this person prefers (or can/cannot) work.
          </p>
        ) : (
          <>
            <Field label="Default location">
              <Select
                value={prefs.default_location_id || "none"}
                onValueChange={(v) =>
                  set({ default_location_id: v === "none" ? null : v })
                }>
                <SelectTrigger className="h-10 rounded-lg"
                                data-testid="emp-default-location">
                  <SelectValue placeholder="No default" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">- No default -</SelectItem>
                  {activeLocations.map((l) => (
                    <SelectItem key={l.location_id} value={l.location_id}
                                 data-testid={`emp-default-location-opt-${l.location_id}`}>
                      {l.name}{l.code ? ` (${l.code})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <div className="mt-3">
              <Label className="eyebrow">Preferred locations</Label>
              <div className="flex flex-wrap gap-1.5 mt-2"
                    data-testid="emp-pref-locs">
                {activeLocations.map((l) => {
                  const on = (prefs.preferred_location_ids || []).includes(l.location_id);
                  return (
                    <button type="button" key={`pl-${l.location_id}`}
                      data-testid={`emp-pref-loc-${l.location_id}`}
                      aria-pressed={on}
                      onClick={() => toggleLocation("preferred_location_ids", l.location_id)}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors inline-flex items-center gap-1 ${
                        on
                          ? "bg-[#2563EB] text-white border-[#2563EB]"
                          : "bg-white border-slate-200 text-[#4B5773] hover:bg-slate-50"
                      }`}>
                      <span className="inline-block w-2 h-2 rounded-full"
                            style={{ backgroundColor: l.colour || "#64748B" }} />
                      {l.name}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-3">
              <Label className="eyebrow">Allowed locations</Label>
              <p className="text-[11px] text-[#4B5773] mt-1">
                If you set any, this person is implicitly restricted to these
                locations. Leave empty to allow all (except restricted).
              </p>
              <div className="flex flex-wrap gap-1.5 mt-2"
                    data-testid="emp-allow-locs">
                {activeLocations.map((l) => {
                  const on = (prefs.allowed_location_ids || []).includes(l.location_id);
                  return (
                    <button type="button" key={`al-${l.location_id}`}
                      data-testid={`emp-allow-loc-${l.location_id}`}
                      aria-pressed={on}
                      onClick={() => toggleLocation("allowed_location_ids", l.location_id)}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors inline-flex items-center gap-1 ${
                        on
                          ? "bg-emerald-100 text-emerald-700 border-emerald-300"
                          : "bg-white border-slate-200 text-[#4B5773] hover:bg-slate-50"
                      }`}>
                      <span className="inline-block w-2 h-2 rounded-full"
                            style={{ backgroundColor: l.colour || "#64748B" }} />
                      {l.name}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-3">
              <Label className="eyebrow">Restricted locations</Label>
              <p className="text-[11px] text-[#4B5773] mt-1">
                Shows a warning when this person is scheduled at one of these
                locations. Restricted locations always take priority over Allowed
                and Preferred.
              </p>
              <div className="flex flex-wrap gap-1.5 mt-2"
                    data-testid="emp-restrict-locs">
                {activeLocations.map((l) => {
                  const on = (prefs.restricted_location_ids || []).includes(l.location_id);
                  return (
                    <button type="button" key={`rl-${l.location_id}`}
                      data-testid={`emp-restrict-loc-${l.location_id}`}
                      aria-pressed={on}
                      onClick={() => toggleLocation("restricted_location_ids", l.location_id)}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors inline-flex items-center gap-1 ${
                        on
                          ? "bg-red-100 text-red-700 border-red-300"
                          : "bg-white border-slate-200 text-[#4B5773] hover:bg-slate-50"
                      }`}>
                      <span className="inline-block w-2 h-2 rounded-full"
                            style={{ backgroundColor: l.colour || "#64748B" }} />
                      {l.name}
                    </button>
                  );
                })}
              </div>
            </div>

            {conflictingLocIds.length > 0 && (
              <div className="mt-3 p-2 rounded-md bg-amber-50 border border-amber-200 text-[11px] text-amber-800"
                    data-testid="emp-loc-conflict-note">
                <MapPin className="inline h-3 w-3 mr-1" />
                <strong>Note:</strong>{" "}
                {conflictingLocIds.length} location
                {conflictingLocIds.length === 1 ? " is" : "s are"} in both Allowed
                and Restricted. Restricted will take precedence.
              </div>
            )}
          </>
        )}
      </Section>

      <div className="p-3 rounded-lg bg-[#F6F9FE] border border-[#DBE7FE] text-xs text-[#1F2A44]"
        data-testid="emp-pref-closing">
        Filling these in helps managers plan fairer rotas and unlocks future automation that
        takes individual preferences into account. Managers always make the final scheduling decisions.
      </div>

      {/* Restrictions */}
      <Section title="Restrictions"
        action={
          <Button type="button" onClick={openAddRestriction}
            data-testid="emp-restriction-add-btn"
            className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8] h-8 text-xs">
            <Plus className="h-3.5 w-3.5 mr-1" />Add restriction
          </Button>
        }>
        {(prefs.restrictions || []).length === 0 ? (
          <p className="text-xs text-[#4B5773]" data-testid="emp-restrictions-empty">
            No restrictions recorded.
          </p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {prefs.restrictions.map((r) => {
              const meta = KIND_BY_VALUE[r.kind] || KIND_BY_VALUE.other;
              return (
                <li key={r.restriction_id}
                  data-testid={`emp-restriction-row-${r.restriction_id}`}
                  className="py-2.5 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge tone={meta.tone}
                        testid={`emp-restriction-kind-${r.restriction_id}`}>
                        <AlertCircle className="h-3 w-3 mr-1" />{meta.label}
                      </Badge>
                      <span className="text-sm font-medium text-[#0B1A36]">{r.label}</span>
                    </div>
                    {(r.starts_on || r.ends_on) && (
                      <div className="text-[11px] text-[#4B5773] mt-1 flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {r.starts_on || "…"} → {r.ends_on || "ongoing"}
                      </div>
                    )}
                    {r.notes && (
                      <div className="text-[11px] text-[#4B5773] mt-1 truncate">{r.notes}</div>
                    )}
                  </div>
                  <div className="space-x-1 whitespace-nowrap">
                    <Button type="button" variant="ghost" size="sm" onClick={() => openEditRestriction(r)}
                      data-testid={`emp-restriction-edit-${r.restriction_id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeRestriction(r)}
                      data-testid={`emp-restriction-remove-${r.restriction_id}`}>
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Section>

      {/* Notes */}
      <Section title="Notes">
        <Input value={prefs.notes || ""}
          onChange={(e) => set({ notes: e.target.value })}
          placeholder="Anything else admins should remember about this person's preferences"
          data-testid="emp-pref-notes"
          className="h-10 rounded-lg" />
      </Section>

      {/* Footer actions */}
      <div className="flex items-center justify-between flex-wrap gap-3 pt-2 border-t border-slate-100">
        <div className="text-[11px] text-[#4B5773]" data-testid="emp-pref-ack-state">
          {prefs.last_acknowledged_at ? (
            <span className="inline-flex items-center gap-1 text-emerald-700">
              <ShieldCheck className="h-3.5 w-3.5" />
              Acknowledged on {prefs.last_acknowledged_at.slice(0, 10)}
            </span>
          ) : (
            <span>Not yet acknowledged.</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" onClick={acknowledge}
            data-testid="emp-pref-acknowledge-btn"
            className="rounded-lg h-9">
            <ShieldCheck className="h-4 w-4 mr-1" />Mark as acknowledged
          </Button>
          <Button type="button" onClick={save} disabled={busy}
            data-testid="emp-pref-save-btn"
            className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8] h-9">
            {busy ? "Saving…" : "Save preferences"}
          </Button>
        </div>
      </div>

      {/* Restriction dialog */}
      <Dialog open={resOpen} onOpenChange={setResOpen}>
        <DialogContent data-testid="emp-restriction-dialog">
          <DialogHeader>
            <DialogTitle>{editingRes ? "Edit restriction" : "Add restriction"}</DialogTitle>
            <DialogDescription>
              Record any restrictions managers should keep in mind when
              scheduling this person, for example medical or contractual limits.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitRestriction} className="space-y-4">
            <div>
              <Label className="eyebrow">Label</Label>
              <Input required value={resForm.label}
                onChange={(e) => setResForm({ ...resForm, label: e.target.value })}
                placeholder="e.g. Cannot lift over 10kg"
                data-testid="emp-restriction-label"
                className="h-11 rounded-lg mt-2" />
            </div>
            <div>
              <Label className="eyebrow">Kind</Label>
              <Select value={resForm.kind}
                onValueChange={(v) => setResForm({ ...resForm, kind: v })}>
                <SelectTrigger className="h-11 rounded-lg mt-2"
                  data-testid="emp-restriction-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RESTRICTION_KINDS.map((k) => (
                    <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Starts on (optional)">
                <Input type="date" value={resForm.starts_on}
                  onChange={(e) => setResForm({ ...resForm, starts_on: e.target.value })}
                  data-testid="emp-restriction-starts"
                  className="h-10 rounded-lg" />
              </Field>
              <Field label="Ends on (optional)">
                <Input type="date" value={resForm.ends_on}
                  onChange={(e) => setResForm({ ...resForm, ends_on: e.target.value })}
                  data-testid="emp-restriction-ends"
                  className="h-10 rounded-lg" />
              </Field>
            </div>
            <div>
              <Label className="eyebrow">Notes (optional)</Label>
              <Input value={resForm.notes}
                onChange={(e) => setResForm({ ...resForm, notes: e.target.value })}
                data-testid="emp-restriction-notes"
                className="h-10 rounded-lg mt-2" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={resBusy}
                data-testid="emp-restriction-submit"
                className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">
                {resBusy ? "Saving…" : editingRes ? "Save changes" : "Add restriction"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ title, action, children }) {
  return (
    <div className="p-4 rounded-lg bg-white border border-slate-200">
      <div className="flex items-center justify-between mb-3">
        <div className="eyebrow">{title}</div>
        {action}
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="eyebrow">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function Badge({ tone = "slate", children, testid }) {
  const TONES = {
    green:  "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber:  "bg-amber-50 text-amber-700 border-amber-200",
    red:    "bg-red-50 text-red-700 border-red-200",
    blue:   "bg-[#DBE7FE] text-[#1D4ED8] border-[#BFD3FB]",
    purple: "bg-violet-50 text-violet-700 border-violet-200",
    slate:  "bg-slate-100 text-slate-600 border-slate-200",
  };
  return (
    <span data-testid={testid}
      className={`inline-flex items-center text-[11px] uppercase tracking-widest font-medium px-2 py-0.5 rounded-full border ${TONES[tone] || TONES.slate}`}>
      {children}
    </span>
  );
}
